import { DayOfWeek, RegularHoursPeriod, TimeOfDay } from '@vendasta/listing-products';

export { ListingProfile } from '@vendasta/listing-products';

function is24Hours(hourPeriod: RegularHoursPeriod) {
  return timeIsMidnight(hourPeriod.openTime) && timeIsMidnight(hourPeriod.closeTime);
}

function timeIsMidnight(tod: TimeOfDay) {
  if ((tod.hours === 0 || tod.hours === 24) && tod.minutes === 0) {
    return true;
  }
  return false;
}

function transformUndefinedTimeObject(rhp: RegularHoursPeriod): RegularHoursPeriod {
  if (!rhp.openTime) {
    rhp.openTime = {
      hours: 0,
      minutes: 0,
      seconds: 0,
      nanos: 0,
      toApiJson: undefined,
    };
  }
  if (!rhp.openTime.hours) {
    rhp.openTime.hours = 0;
  }
  if (!rhp.openTime.minutes) {
    rhp.openTime.minutes = 0;
  }
  if (!rhp.closeTime) {
    rhp.closeTime = {
      hours: 0,
      minutes: 0,
      seconds: 0,
      nanos: 0,
      toApiJson: undefined,
    };
  }
  if (!rhp.closeTime.hours) {
    rhp.closeTime.hours = 0;
  }
  if (!rhp.closeTime.minutes) {
    rhp.closeTime.minutes = 0;
  }

  return rhp;
}

function bIsNextDayToA(a: DayOfWeek, b: DayOfWeek) {
  if (a + 1 === b) {
    return true;
  }
  return a === DayOfWeek.SUNDAY && b === DayOfWeek.MONDAY;
}

export function MergeHours(regularHours: RegularHoursPeriod[]): RegularHoursPeriod[] {
  const mergedHours: RegularHoursPeriod[] = [];
  for (let i = 0; i < regularHours.length; i++) {
    let combiningWrap = false;
    let curr = regularHours[i];
    let next = regularHours[i + 1];

    // Special case for crossing midnight hours on sunday to Monday
    if (curr.openDay === DayOfWeek.SUNDAY && curr.closeDay === DayOfWeek.MONDAY) {
      combiningWrap = true;
      next = regularHours[0];
    }
    // Unfuck shitty typescript objects
    if (curr) {
      curr = transformUndefinedTimeObject(curr);
    }
    if (next) {
      next = transformUndefinedTimeObject(next);
    }

    if (
      next && // There must be a next entry to paste to
      !is24Hours(curr) && // Don't combine if either span is 24 hours
      !is24Hours(next) &&
      bIsNextDayToA(curr.openDay, next.openDay) && // Spans must be consecutive days
      timeIsMidnight(curr.closeTime) && // Span must end at midnight
      timeIsMidnight(next.openTime) && // Next span must start at midnight
      // Merged span must be less than or equal to 24 hours
      (curr.openTime.hours > next.closeTime.hours ||
        (curr.openTime.hours === next.closeTime.hours && curr.openTime.minutes >= next.closeTime.minutes))
    ) {
      const cs: RegularHoursPeriod = {
        openDay: curr.openDay,
        openTime: curr.openTime,
        closeDay: next.closeDay,
        closeTime: next.closeTime,
        toApiJson(): object {
          return undefined;
        },
      };
      mergedHours.push(cs);
      // For the special case of the last span combining with the first, we
      // also need to go back and remove the first
      if (combiningWrap) {
        mergedHours.shift();
      }
      i++; // skip the next span since it has been combined with the current span
    } else {
      mergedHours.push(curr);
    }
  }
  return mergedHours;
}

export function SplitHours(timeSpanList: RegularHoursPeriod[]): RegularHoursPeriod[] {
  return timeSpanList.reduce((acc: RegularHoursPeriod[], curr: RegularHoursPeriod) => {
    if (curr.openTime.hours < curr.closeTime.hours) {
      acc.push(curr);
      return acc;
    }

    const preMidnightHoursPeriod = new RegularHoursPeriod({
      ...curr,
      closeTime: new TimeOfDay({ hours: 24, minutes: 0 }),
    });

    acc.push(preMidnightHoursPeriod);

    if (curr.closeTime.hours === 0) {
      return acc;
    }

    const nextDay = curr.closeDay + 1 > DayOfWeek.SUNDAY ? 1 : curr.closeDay + 1;

    const crossMidnightHoursPeriod = new RegularHoursPeriod({
      ...curr,
      openTime: new TimeOfDay({ hours: 0, minutes: 0 }),
      closeDay: nextDay,
      openDay: nextDay,
    });

    acc.push(crossMidnightHoursPeriod);
    return acc;
  }, []);
}
